import type {
  INeoConversationService,
  NeoConversationError,
} from "../ports/driver/neoConversationService";
import type { INeoConversationRepository } from "../ports/driven/neoConversationRepository";

const toError = (error: unknown): NeoConversationError => {
  const fields =
    error && typeof error === "object"
      ? (error as { status?: unknown; code?: unknown; kind?: unknown })
      : {};
  return {
    message: error instanceof Error ? error.message : "Something went wrong.",
    status: typeof fields.status === "number" ? fields.status : undefined,
    code: typeof fields.code === "string" ? fields.code : undefined,
    kind: typeof fields.kind === "string" ? fields.kind : undefined,
  };
};

export const NeoConversationServiceImplFactory = ({
  neoConversationRepository,
}: {
  neoConversationRepository: INeoConversationRepository;
}): INeoConversationService => ({
  listConversations: async () => {
    try {
      const response = await neoConversationRepository.listConversationsAPI();
      return { success: true, data: response.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  listConversationActivity: async (params) => {
    try {
      const data = await neoConversationRepository.listConversationActivityAPI(
        params
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  markConversationViewed: async (conversationId, throughSequenceNum) => {
    try {
      const data = await neoConversationRepository.markConversationViewedAPI(
        conversationId,
        throughSequenceNum
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  getConversationHistory: async (conversationId) => {
    try {
      const data = await neoConversationRepository.getConversationHistoryAPI(
        conversationId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  updateConversationTitle: async (conversationId, title) => {
    try {
      const data = await neoConversationRepository.updateConversationTitleAPI(
        conversationId,
        title
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  createConversation: async (payload) => {
    try {
      const data = await neoConversationRepository.createConversationAPI(
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  deleteConversation: async (conversationId) => {
    try {
      const resp = await neoConversationRepository.deleteConversationAPI(
        conversationId
      );
      // The transport resolves a failed DELETE with its error body (FastAPI
      // `{ detail }`); require `{ deleted: true }` so callers don't commit an
      // optimistic removal that never happened.
      if ((resp as { deleted?: unknown } | null)?.deleted !== true) {
        const detail = (resp as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "Could not delete the chat.",
          },
        };
      }
      return { success: true, data: undefined };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  uploadFile: async (conversationId, file) => {
    try {
      const data = await neoConversationRepository.uploadFileAPI(
        conversationId,
        file
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
  streamTurn: async (conversationId, payload, onFrame, signal) => {
    try {
      await neoConversationRepository.streamTurnAPI(
        conversationId,
        payload,
        onFrame,
        signal
      );
      return { success: true, data: undefined };
    } catch (error) {
      if (signal.aborted) return { success: true, data: undefined };
      return { success: false, error: toError(error) };
    }
  },
  cancelMessage: async (messageId, cause) => {
    try {
      await neoConversationRepository.cancelMessageAPI(messageId, cause);
      return { success: true, data: undefined };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  },
});
